/**
 * Procedural audio primitives (DESIGN 6.8, 7.3 audio/synth.ts): one
 * AudioContext created and resumed on the first user gesture, a master
 * gain, a generated impulse response for a short reverb, stereo panning by
 * x offset, oscillator / noise / loop voices with attack-release envelopes,
 * and a hard cap of CLIENT.maxVoices (24) simultaneous voices: when the cap
 * is reached a new voice is dropped rather than clipping the mix.
 *
 * No audio files, no DOM beyond the Web Audio API; everything is built from
 * oscillators, a white-noise buffer and biquad filters.
 */

import { CLIENT } from '@shared/index.js';

export interface VoiceOpts {
  /** Peak gain 0..1 (default 0.3). */
  gain?: number;
  /** Stereo position -1..1 (default 0). */
  pan?: number;
  /** Start delay in seconds (default 0). */
  at?: number;
  /** Reverb send 0..1 (default 0). */
  reverb?: number;
  attack?: number;
  release?: number;
}

export interface ToneOpts extends VoiceOpts {
  freq: number;
  type?: OscillatorType;
  dur: number;
  /** Frequency to glide to over `glideTime` seconds (default: the whole duration). */
  glideTo?: number;
  glideTime?: number;
  detune?: number;
  lowpass?: number;
  highpass?: number;
  tremoloHz?: number;
  /** 0..1 amplitude depth of the tremolo. */
  tremoloDepth?: number;
}

export interface NoiseOpts extends VoiceOpts {
  dur: number;
  filter?: BiquadFilterType;
  freq?: number;
  /** Filter frequency to sweep to over the duration (pitch drops for explosions). */
  freqTo?: number;
  q?: number;
}

export interface LoopOpts {
  kind: 'tone' | 'noise';
  freq: number;
  type?: OscillatorType;
  gain: number;
  pan?: number;
  filter?: BiquadFilterType;
  filterFreq?: number;
  q?: number;
  tremoloHz?: number;
  tremoloDepth?: number;
  attack?: number;
}

/** A sustained voice: beam hum, burning crackle, charge rise. */
export interface LoopHandle {
  readonly stopped: boolean;
  setGain(gain: number, rampSeconds?: number): void;
  setFreq(freq: number, rampSeconds?: number): void;
  setPan(pan: number): void;
  stop(releaseSeconds?: number): void;
}

const NOISE_SECONDS = 2;
const IMPULSE_SECONDS = 0.45;
const IMPULSE_DECAY = 4;
/** A gentle high-shelf cut on the effects bus: recordings with bright transients stop reading as harsh. */
const SFX_SHELF_HZ = 5500;
const SFX_SHELF_DB = -3.5;
const DEFAULT_GAIN = 0.3;
const DEFAULT_ATTACK = 0.005;
const DEFAULT_RELEASE = 0.05;
const MIN_LEVEL = 0.0001;

type AudioContextCtor = new () => AudioContext;

function resolveContext(): AudioContextCtor | null {
  const g = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

export function clampPan(x: number): number {
  return x < -1 ? -1 : x > 1 ? 1 : x;
}

/** Exponentially decaying stereo noise: a small room, enough for a 0.3 s tail (DESIGN 6.8). */
export function makeImpulse(ctx: BaseAudioContext, seconds = IMPULSE_SECONDS, decay = IMPULSE_DECAY): AudioBuffer {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buffer = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buffer;
}

function makeNoise(ctx: BaseAudioContext): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * NOISE_SECONDS);
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}

/** The three mix buses under the master: the synth's voices and the samples share them. */
export type Bus = 'sfx' | 'music' | 'ambience';

export class Synth {
  readonly maxVoices: number = CLIENT.maxVoices;

  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private buses: Record<Bus, GainNode> | null = null;
  private busVolume: Record<Bus, number> = { sfx: 1, music: 1, ambience: 1 };
  private reverbIn: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private active = 0;
  private volume = 1;
  private failed = false;

  /** True once the context exists and is running (after the first gesture). */
  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Voices playing right now (loops count until stopped). */
  get voices(): number {
    return this.active;
  }

  get currentTime(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  /** Create the context on the first user gesture and resume it whenever the browser suspended it. */
  resume(): void {
    if (this.failed) return;
    if (!this.ctx) {
      const Ctor = resolveContext();
      if (!Ctor) {
        this.failed = true;
        return;
      }
      try {
        this.build(new Ctor());
      } catch {
        this.failed = true;
        return;
      }
    }
    const ctx = this.ctx;
    if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => { /* needs another gesture */ });
  }

  /** Master volume 0..1, ramped so toggling never clicks. */
  setVolume(v: number): void {
    this.volume = clamp01(v);
    if (this.ctx && this.master) this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
  }

  /** One bus's level 0..1 (the sound settings' sliders). */
  setBusVolume(bus: Bus, v: number): void {
    this.busVolume[bus] = clamp01(v);
    if (this.ctx && this.buses) this.buses[bus].gain.setTargetAtTime(this.busVolume[bus], this.ctx.currentTime, 0.02);
  }

  /** The context, for sample playback (null before the first gesture). */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /** Where a sample connects: the bus's gain node (null before the first gesture). */
  bus(name: Bus): GainNode | null {
    return this.buses ? this.buses[name] : null;
  }

  /** The short room on the effects bus; a sample sends a little of itself here so it sits in the arena. */
  get reverbSend(): GainNode | null {
    return this.reverbIn;
  }

  /** A single oscillator note with an attack-release envelope. Returns false when dropped (cap or no context). */
  tone(o: ToneOpts): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.acquire()) return false;
    const t0 = ctx.currentTime + (o.at ?? 0);
    const osc = ctx.createOscillator();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(Math.max(1, o.freq), t0);
    if (o.detune) osc.detune.setValueAtTime(o.detune, t0);
    if (o.glideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, o.glideTo), t0 + (o.glideTime ?? o.dur));
    let head: AudioNode = osc;
    if (o.lowpass) head = this.filter(head, 'lowpass', o.lowpass, t0);
    if (o.highpass) head = this.filter(head, 'highpass', o.highpass, t0);
    if (o.tremoloHz && o.tremoloDepth) head = this.tremolo(head, o.tremoloHz, o.tremoloDepth, t0, t0 + o.dur);
    const env = this.envelope(head, o, t0, o.dur);
    this.route(env, o.pan, o.reverb);
    osc.onended = () => this.release();
    osc.start(t0);
    osc.stop(t0 + o.dur + (o.release ?? DEFAULT_RELEASE) + 0.02);
    return true;
  }

  /** Filtered white noise with an envelope; `freqTo` sweeps the filter (pitch drop). */
  noise(o: NoiseOpts): boolean {
    const ctx = this.ctx;
    if (!ctx || !this.noiseBuffer || !this.acquire()) return false;
    const t0 = ctx.currentTime + (o.at ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;
    src.loop = true;
    src.loopStart = Math.random() * (NOISE_SECONDS * 0.5);
    let head: AudioNode = src;
    if (o.filter) {
      const f = ctx.createBiquadFilter();
      f.type = o.filter;
      f.frequency.setValueAtTime(Math.max(10, o.freq ?? 1000), t0);
      if (o.freqTo !== undefined) f.frequency.exponentialRampToValueAtTime(Math.max(10, o.freqTo), t0 + o.dur);
      f.Q.setValueAtTime(o.q ?? 1, t0);
      head.connect(f);
      head = f;
    }
    const env = this.envelope(head, o, t0, o.dur);
    this.route(env, o.pan, o.reverb);
    src.onended = () => this.release();
    src.start(t0);
    src.stop(t0 + o.dur + (o.release ?? DEFAULT_RELEASE) + 0.02);
    return true;
  }

  /** Several tones at once (nova chords, chimes); each note counts as a voice. */
  chord(freqs: readonly number[], o: Omit<ToneOpts, 'freq'>): void {
    for (const freq of freqs) this.tone({ ...o, freq });
  }

  /** A sustained voice the caller shapes over time; null when dropped. */
  loop(o: LoopOpts): LoopHandle | null {
    const ctx = this.ctx;
    if (!ctx || !this.acquire()) return null;
    if (o.kind === 'noise' && !this.noiseBuffer) {
      this.release();
      return null;
    }
    const t0 = ctx.currentTime;
    let source: OscillatorNode | AudioBufferSourceNode;
    if (o.kind === 'tone') {
      const osc = ctx.createOscillator();
      osc.type = o.type ?? 'sine';
      osc.frequency.setValueAtTime(Math.max(1, o.freq), t0);
      source = osc;
    } else {
      const src = ctx.createBufferSource();
      src.buffer = this.noiseBuffer;
      src.loop = true;
      source = src;
    }
    let head: AudioNode = source;
    let filterNode: BiquadFilterNode | null = null;
    if (o.filter) {
      filterNode = ctx.createBiquadFilter();
      filterNode.type = o.filter;
      filterNode.frequency.setValueAtTime(Math.max(10, o.filterFreq ?? o.freq), t0);
      filterNode.Q.setValueAtTime(o.q ?? 1, t0);
      head.connect(filterNode);
      head = filterNode;
    }
    if (o.tremoloHz && o.tremoloDepth) head = this.tremolo(head, o.tremoloHz, o.tremoloDepth, t0, null);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(MIN_LEVEL, t0);
    gain.gain.exponentialRampToValueAtTime(Math.max(MIN_LEVEL, o.gain), t0 + (o.attack ?? 0.05));
    head.connect(gain);
    const panner = this.route(gain, o.pan, 0);
    source.start(t0);
    let stopped = false;
    const release = (): void => {
      if (stopped) return;
      stopped = true;
      this.release();
    };
    source.onended = release;
    return {
      get stopped(): boolean { return stopped; },
      setGain: (g, ramp = 0.05) => {
        if (stopped) return;
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setTargetAtTime(Math.max(MIN_LEVEL, g), ctx.currentTime, ramp);
      },
      setFreq: (f, ramp = 0.05) => {
        if (stopped) return;
        const target = Math.max(1, f);
        if (source instanceof OscillatorNode) source.frequency.setTargetAtTime(target, ctx.currentTime, ramp);
        if (filterNode) filterNode.frequency.setTargetAtTime(Math.max(10, o.kind === 'noise' ? target : o.filterFreq ?? target), ctx.currentTime, ramp);
      },
      setPan: (p) => {
        if (!stopped && panner) panner.pan.setTargetAtTime(clampPan(p), ctx.currentTime, 0.05);
      },
      stop: (rel = 0.08) => {
        if (stopped) return;
        const now = ctx.currentTime;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setTargetAtTime(MIN_LEVEL, now, rel / 3);
        source.stop(now + rel + 0.05);
      },
    };
  }

  // ---------------------------------------------------------------- internals

  private build(ctx: AudioContext): void {
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = this.volume;
    // A soft limiter on the master: stacked hits and a stinger together never clip. The wide knee
    // and slower release keep it from pumping audibly when a busy moment leans on it.
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -8;
    limiter.knee.value = 10;
    limiter.ratio.value = 12;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;
    master.connect(limiter);
    limiter.connect(ctx.destination);
    this.master = master;
    // The effects bus runs through a high-shelf cut before the master; music and ambience go straight in.
    const shelf = ctx.createBiquadFilter();
    shelf.type = 'highshelf';
    shelf.frequency.value = SFX_SHELF_HZ;
    shelf.gain.value = SFX_SHELF_DB;
    shelf.connect(master);
    const mk = (name: Bus, to: AudioNode): GainNode => {
      const g = ctx.createGain();
      g.gain.value = this.busVolume[name];
      g.connect(to);
      return g;
    };
    this.buses = { sfx: mk('sfx', shelf), music: mk('music', master), ambience: mk('ambience', master) };
    const convolver = ctx.createConvolver();
    convolver.buffer = makeImpulse(ctx);
    const reverbIn = ctx.createGain();
    reverbIn.gain.value = 1;
    reverbIn.connect(convolver);
    convolver.connect(this.buses.sfx);
    this.reverbIn = reverbIn;
    this.noiseBuffer = makeNoise(ctx);
  }

  private acquire(): boolean {
    if (!this.ctx || this.ctx.state !== 'running') return false;
    if (this.active >= this.maxVoices) return false;
    this.active++;
    return true;
  }

  private release(): void {
    if (this.active > 0) this.active--;
  }

  private filter(input: AudioNode, type: BiquadFilterType, freq: number, t0: number): AudioNode {
    const ctx = this.ctx as AudioContext;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(Math.max(10, freq), t0);
    input.connect(f);
    return f;
  }

  /** Amplitude modulation: output = input x (1 - depth + depth x lfo). */
  private tremolo(input: AudioNode, hz: number, depth: number, t0: number, until: number | null): AudioNode {
    const ctx = this.ctx as AudioContext;
    const amp = ctx.createGain();
    amp.gain.setValueAtTime(1 - depth, t0);
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(hz, t0);
    const lfoGain = ctx.createGain();
    lfoGain.gain.setValueAtTime(depth, t0);
    lfo.connect(lfoGain);
    lfoGain.connect(amp.gain);
    lfo.start(t0);
    if (until !== null) lfo.stop(until + 0.1);
    input.connect(amp);
    return amp;
  }

  /** Attack / hold / release gain envelope ending at MIN_LEVEL. */
  private envelope(input: AudioNode, o: VoiceOpts, t0: number, dur: number): GainNode {
    const ctx = this.ctx as AudioContext;
    const gain = ctx.createGain();
    const peak = Math.max(MIN_LEVEL, o.gain ?? DEFAULT_GAIN);
    const attack = Math.min(o.attack ?? DEFAULT_ATTACK, dur);
    const release = o.release ?? DEFAULT_RELEASE;
    gain.gain.setValueAtTime(MIN_LEVEL, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + attack);
    gain.gain.setValueAtTime(peak, t0 + dur);
    gain.gain.exponentialRampToValueAtTime(MIN_LEVEL, t0 + dur + release);
    input.connect(gain);
    return gain;
  }

  /** Pan and connect to the sfx bus (dry) and, at `reverb` level, to the convolver. Returns the panner when one exists. */
  private route(input: AudioNode, pan: number | undefined, reverb: number | undefined): StereoPannerNode | null {
    const ctx = this.ctx as AudioContext;
    const master = (this.buses ? this.buses.sfx : this.master) as GainNode;
    let out: AudioNode = input;
    let panner: StereoPannerNode | null = null;
    if (pan !== undefined && typeof ctx.createStereoPanner === 'function') {
      panner = ctx.createStereoPanner();
      panner.pan.setValueAtTime(clampPan(pan), ctx.currentTime);
      out.connect(panner);
      out = panner;
    }
    out.connect(master);
    if (reverb && reverb > 0 && this.reverbIn) {
      const send = ctx.createGain();
      send.gain.value = clamp01(reverb);
      out.connect(send);
      send.connect(this.reverbIn);
    }
    return panner;
  }
}

let singleton: Synth | null = null;

/** The one Synth of the page; created lazily, context opened on resume(). */
export function getSynth(): Synth {
  if (!singleton) singleton = new Synth();
  return singleton;
}
